import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated counter that flips through numbers and slows down to the target value.
 * Creates a smooth "slot machine" effect with visual flip animation.
 */
export default function AnimatedCounter({ 
  value, 
  duration = 2000, 
  decimals = 0,
  className = '' 
}) {
  const targetValue = Number(value) || 0;
  const formattedTarget = decimals > 0 ? targetValue.toFixed(decimals) : Math.floor(targetValue);
  
  const [displayValue, setDisplayValue] = useState(formattedTarget);
  const [isAnimating, setIsAnimating] = useState(false);
  const [flipKey, setFlipKey] = useState(0);
  const startTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const previousValueRef = useRef(targetValue);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const target = Number(value) || 0;
    
    // First render - initialize
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      previousValueRef.current = 0;  // Always start from 0 on init
      setDisplayValue(decimals > 0 ? '0.0' : '0');
      
      // If target is also 0, we're done
      if (target === 0) return;
    }
    
    const startValue = previousValueRef.current;
    
    // If value hasn't changed, don't animate again
    if (target === startValue) {
      return;
    }

    // Animate from startValue to target
    previousValueRef.current = target;
    setIsAnimating(true);
    setFlipKey(prev => prev + 1);
    startTimeRef.current = null;

    // Easing function: ease-out cubic for smooth deceleration
    const easeOutCubic = (t) => {
      return 1 - Math.pow(1 - t, 3);
    };

    const animate = (currentTime) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Apply easing for smooth deceleration
      const easedProgress = easeOutCubic(progress);
      
      // Calculate current value - animate from startValue to target
      const currentValue = startValue + (target - startValue) * easedProgress;
      
      // Format based on decimals
      const formatted = decimals > 0 
        ? currentValue.toFixed(decimals) 
        : Math.floor(currentValue);
      
      setDisplayValue(String(formatted));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at target value
        const finalValue = decimals > 0 
          ? target.toFixed(decimals) 
          : Math.floor(target);
        setDisplayValue(String(finalValue));
        setIsAnimating(false);
        previousValueRef.current = target;
      }
    };

    // Start animation if value changed or on first init with value > 0
    if (hasInitialized.current) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (target > 0) {
      // First render with value > 0, start animation
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value, duration, decimals, formattedTarget]);

  return (
    <>
      <style>{`
        @keyframes flipNumber {
          0% {
            transform: rotateX(0deg) scale(1);
            opacity: 1;
          }
          50% {
            transform: rotateX(90deg) scale(0.95);
            opacity: 0.6;
          }
          100% {
            transform: rotateX(0deg) scale(1);
            opacity: 1;
          }
        }
        .animated-counter-flip {
          animation: flipNumber 0.6s ease-out;
          transform-style: preserve-3d;
          perspective: 1000px;
        }
      `}</style>
      <span 
        className={`inline-block ${isAnimating ? 'animated-counter-flip' : ''} ${className}`}
        key={flipKey}
        style={{
          display: 'inline-block',
          transformStyle: 'preserve-3d',
        }}
      >
        {displayValue}
      </span>
    </>
  );
}
